/**
 * A TERMINAL BLOCK (RFC 0276 S2): one machine's shells, in a pane.
 *
 * The same shape as the agent block, deliberately. On no shell it is that
 * machine's LIST, with a row that starts another; on a shell it is that shell,
 * and the back button returns to the list. That is what lets a second terminal
 * cost a selection rather than a pane, and it is why this is not tabs bolted
 * onto a pane: the project already answered this question once, for sessions.
 *
 * A PANE IS A VIEW OF A SHELL, NOT THE SHELL. Closing this pane closes a socket;
 * the pty keeps running on the machine and is re-attachable from the list, from
 * a link, or after a refresh, with its scrollback replayed. Ending a shell is a
 * separate, named action — the only control here that destroys something you
 * cannot get back.
 */

import { FitAddon } from '@xterm/addon-fit'
import { WebglAddon } from '@xterm/addon-webgl'
import { Terminal } from '@xterm/xterm'
import {
  ChevronLeft,
  Columns2,
  PanelLeftOpen,
  Plus,
  Power,
  TerminalIcon,
  X,
} from 'lucide-react'
import {
  Component,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import '@xterm/xterm/css/xterm.css'
import { Panel, PanelAction } from '../../shell/window/Panel'
import {
  createTerminal,
  endTerminal,
  type LocalTerminals,
  localTerminalTransport,
} from './termClient'
import { useLocalTerminals } from './useLocalTerminals'

export function TerminalPanel({
  projectId,
  deviceId,
  terminalId,
  autoStart = false,
  onSelect,
  onSplit,
  onClose,
  sidebarCollapsed,
  onShowSidebar,
  focused = false,
}: {
  /** Whose checkout the shells are in (RFC 0331 3a). */
  projectId: string
  deviceId: string
  /** Null is the machine's list of shells. */
  terminalId: string | null
  /** This pane was opened by "New terminal", so start one instead of showing
   *  the list. Fires at most once per pane. */
  autoStart?: boolean
  /** One block changing what it shows: the back button, and picking a shell. */
  onSelect: (id: string | null) => void
  onSplit?: () => void
  onClose?: () => void
  sidebarCollapsed: boolean
  onShowSidebar?: () => void
  focused?: boolean
}) {
  const { probe, loading, refresh } = useLocalTerminals(true, projectId)

  /**
   * The moment between asking for a shell and having its id.
   *
   * Short — one localhost round trip — but it is a real state: the pane is
   * neither the list nor a terminal, and without it a slow answer looks like a
   * click that did nothing.
   */
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inShell = terminalId !== null

  /**
   * MINT FIRST, THEN SHOW IT.
   *
   * The shell is created by an explicit request here, and only then does its id
   * go in the url and a view attach to it. The alternative — mount a view on
   * "no shell yet" and let the socket create one — makes spawning a process a
   * side effect of RENDERING, and it does not survive a remount: React's strict
   * mode alone turned every click into two shells, one of them orphaned.
   */
  const startNew = useCallback(() => {
    setError(null)
    setStarting(true)
    // 80x24 is a placeholder the pane corrects the instant it attaches. The
    // real size is a property of a pane that does not exist yet.
    createTerminal(projectId, { cols: 80, rows: 24 })
      .then(({ id }) => {
        onSelect(id)
        refresh()
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setStarting(false))
  }, [onSelect, refresh, projectId])

  const backToList = useCallback(() => {
    setError(null)
    onSelect(null)
    refresh()
  }, [onSelect, refresh])

  const end = useCallback(async () => {
    if (terminalId) await endTerminal(projectId, terminalId)
    backToList()
  }, [terminalId, backToList, projectId])

  const title = probe?.live.find((s) => s.id === terminalId)?.title

  /**
   * WHETHER THE MACHINE THIS LEAF NAMES IS THE ONE ANSWERING HERE.
   *
   * Nothing may attach until this says `here`, and that is a correctness rule
   * rather than a nicety. The server hands a fresh shell to anyone who asks for
   * an id it does not have (which is what heals a link to a shell that has
   * since exited) — so attaching a FOREIGN id to the local server would quietly
   * spawn a local shell and call it that machine's, which is precisely the
   * thing a terminal leaf naming its device for its whole life exists to
   * prevent.
   *
   * Once confirmed it STAYS confirmed. The probe is a five-second poll, and a
   * single missed one is a network hiccup, not a machine that went away — it
   * must not be able to tear down a pane you are typing in. The socket reports
   * its own trouble.
   */
  const [confirmed, setConfirmed] = useState(false)
  useEffect(() => {
    if (probe && probe.deviceId === deviceId) setConfirmed(true)
  }, [probe, deviceId])

  const reach: 'looking' | 'here' | 'elsewhere' | 'none' = confirmed
    ? 'here'
    : loading && !probe
      ? 'looking'
      : !probe
        ? 'none'
        : probe.deviceId === deviceId
          ? 'here'
          : 'elsewhere'

  /**
   * "New terminal" made this pane, so make the terminal.
   *
   * ONCE, and the ref is what guarantees it: `autoStart` stays true for as long
   * as this is the pane that asked, so without the latch the back button would
   * land on the list and be thrown straight back into a new shell — and every
   * bounce would leave another pty running on the machine.
   *
   * Waits for `reach === 'here'`, like everything else that attaches. Starting
   * before the machine has answered is how you spawn a shell somewhere you did
   * not mean to.
   */
  const armed = useRef(false)
  useEffect(() => {
    if (!autoStart || armed.current) return
    if (terminalId !== null || reach !== 'here') return
    armed.current = true
    startNew()
  }, [autoStart, terminalId, reach, startNew])

  return (
    <Panel
      focused={focused}
      framed={false}
      padded={false}
      header={
        <>
          {sidebarCollapsed && onShowSidebar && (
            <PanelAction title="Show files (⌘B)" onClick={onShowSidebar}>
              <PanelLeftOpen size={14} />
            </PanelAction>
          )}
          {inShell && (
            <PanelAction
              title="Back to this machine's shells"
              onClick={backToList}
            >
              <ChevronLeft size={14} />
            </PanelAction>
          )}
          <TerminalIcon size={13} className="shrink-0 text-muted-foreground" />
          <span className="truncate font-medium text-muted-foreground text-sm">
            {inShell ? (title ?? 'Terminal') : 'Terminals'}
          </span>
          <div className="flex-1" />
          {inShell && terminalId && (
            // END, spelled out in its tooltip rather than an X, because it is
            // the one control in this pane that stops a process. Closing the
            // pane is the X, and it leaves the shell running.
            <PanelAction
              title="End this shell (stops the process)"
              onClick={() => void end()}
            >
              <Power size={14} />
            </PanelAction>
          )}
          {!inShell && reach === 'here' && (
            <PanelAction title="New terminal" onClick={startNew}>
              <Plus size={14} />
            </PanelAction>
          )}
          {onSplit && (
            <PanelAction title="Open beside this" onClick={onSplit}>
              <Columns2 size={14} />
            </PanelAction>
          )}
          {onClose && (
            <PanelAction
              title="Close this pane (the shell keeps running)"
              onClick={onClose}
            >
              <X size={14} />
            </PanelAction>
          )}
        </>
      }
    >
      {reach === 'looking' ? (
        <Message>Looking for this machine…</Message>
      ) : reach === 'elsewhere' ? (
        <Message>
          This pane is a terminal on another machine. Reaching one from here is
          not built yet — open it on that machine, or start a shell on this one.
        </Message>
      ) : reach === 'none' ? (
        <Message>
          No terminal server on this machine. Terminals are served by the Mod
          desktop daemon; open this project in the desktop app.
        </Message>
      ) : inShell && terminalId ? (
        /**
         * KEYED BY THE SHELL, which is now safe and was not before: a remount
         * re-attaches to that pty rather than asking for a new one, so the
         * worst a stray remount costs is a reconnect.
         */
        <ShellBoundary key={terminalId}>
          <ShellView
            projectId={projectId}
            shellId={terminalId}
            onFailed={(message) => {
              setError(message)
              onSelect(null)
            }}
          />
        </ShellBoundary>
      ) : starting ? (
        <Message>Starting a shell…</Message>
      ) : (
        <ShellList
          probe={probe}
          error={error}
          onPick={(id) => onSelect(id)}
          onNew={startNew}
        />
      )}
    </Panel>
  )
}

function Message({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pt-3 text-[11px] text-muted-foreground/70">{children}</p>
  )
}

/**
 * NOTHING IN A TERMINAL MAY TAKE THE WINDOW DOWN.
 *
 * This is here because it already happened: xterm's GPU addon threw while
 * activating, React unmounted the whole tree, and the project went blank —
 * every other pane, the sidebar, the session you were in, all gone because one
 * pane could not paint. A terminal is a guest in this window; the emulator is
 * third-party code driving a GPU context, and the honest assumption is that it
 * will fail in ways this app cannot anticipate.
 *
 * A class component because that is still the only way to catch a render error
 * in React. Keyed by the shell above, so a different shell gets a clean one
 * rather than inheriting a previous failure.
 */
class ShellBoundary extends Component<
  { children: ReactNode },
  { failed: string | null }
> {
  state: { failed: string | null } = { failed: null }

  static getDerivedStateFromError(err: unknown) {
    return { failed: err instanceof Error ? err.message : String(err) }
  }

  componentDidCatch(err: unknown) {
    // Visible in the console for whoever is debugging, and nowhere else: the
    // person looking at this pane needs the sentence below, not a stack.
    console.error('[terminal] view failed', err)
  }

  render() {
    if (this.state.failed === null) return this.props.children
    return (
      <Message>
        This terminal could not be drawn ({this.state.failed}). The shell itself
        is still running on this machine — go back and open it again.
      </Message>
    )
  }
}

/** The machine's shells, and a row that starts another. */
function ShellList({
  probe,
  error,
  onPick,
  onNew,
}: {
  /** Never null in practice — the list is only reached once the machine has
   *  answered — but typed so, since it is the probe's own type. */
  probe: LocalTerminals | null
  error: string | null
  onPick: (id: string) => void
  onNew: () => void
}) {
  return (
    <div className="h-full min-h-0 overflow-y-auto p-1">
      {error && <p className="px-2 pt-2 text-[11px] text-red-400">{error}</p>}
      <button
        type="button"
        onClick={onNew}
        className="flex w-full min-w-0 items-center gap-2 rounded px-2 py-1 text-left text-muted-foreground text-sm hover:bg-muted/60 hover:text-foreground"
      >
        <Plus size={14} className="shrink-0" />
        <span className="truncate">
          New terminal{probe?.shell ? ` (${baseName(probe.shell)})` : ''}
        </span>
      </button>
      {probe && probe.live.length > 0 && (
        <>
          <p className="px-2 pt-3 pb-1 text-[11px] text-muted-foreground/60">
            Running
          </p>
          {probe.live.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              title={s.title}
              className="flex w-full min-w-0 items-center gap-2 rounded px-2 py-1 text-left text-muted-foreground text-sm hover:bg-muted/60 hover:text-foreground"
            >
              <TerminalIcon size={14} className="shrink-0" />
              <span className="truncate">{s.title}</span>
            </button>
          ))}
        </>
      )}
      {probe?.cwd && (
        <p className="px-2 pt-3 text-[11px] text-muted-foreground/50">
          Starts in {probe.cwd}
        </p>
      )}
    </div>
  )
}

const baseName = (path: string): string => path.split('/').pop() || path

/**
 * ONE SHELL, drawn by xterm and fed by the transport.
 *
 * Mounted ONCE per shell: everything below runs in an effect with no
 * dependencies, and the id it opens with is captured on mount. A view that
 * re-ran on prop changes would drop the pty and take a second one, which is
 * the failure that would be reported as "it spawns two terminals".
 */
function ShellView({
  projectId,
  shellId,
  onFailed,
}: {
  projectId: string
  /** A shell that ALREADY EXISTS. This view never creates one. */
  shellId: string
  onFailed: (message: string) => void
}) {
  const host = useRef<HTMLDivElement | null>(null)
  const [exited, setExited] = useState<number | null>(null)
  // Read through a ref so the effect below can have no dependencies without
  // capturing a stale callback.
  const idRef = useRef(shellId)
  const projectRef = useRef(projectId)
  const failedRef = useRef(onFailed)
  failedRef.current = onFailed

  useEffect(() => {
    const el = host.current
    if (!el) return

    /**
     * BUILT A FRAME LATE, and that is not a nicety.
     *
     * React's strict mode mounts every effect, tears it down, and mounts it
     * again — so building an emulator synchronously means constructing one and
     * disposing it microseconds later. xterm does not survive that cleanly: its
     * viewport has a queued task that fires after dispose and reads a render
     * service that is gone, which is an uncaught TypeError, and an uncaught
     * error during render is how this pane blanked the whole project.
     *
     * Deferring one frame makes setup ATOMIC with respect to an immediate
     * remount: the throwaway mount cancels the frame and never builds anything.
     * It costs 16ms on a pane that is about to wait on a socket anyway.
     */
    let disposed = false
    let teardown: (() => void) | null = null

    const build = (host: HTMLDivElement): (() => void) => {
      /**
       * OPAQUE, and read from the page rather than hard-coded.
       *
       * The first cut asked for a transparent background so the pane's own
       * surface showed through. That is what made the text look thin next to a
       * real terminal: a transparent backdrop forces greyscale antialiasing,
       * because the renderer cannot know what is behind a glyph and so cannot
       * do subpixel work. Taking the colour off the body gets the theme without
       * paying for it.
       */
      const styles = getComputedStyle(document.body)
      const background = styles.backgroundColor || '#0a0a0a'
      const foreground = styles.color || '#e5e5e5'

      const term = new Terminal({
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: 12,
        theme: { background, foreground, cursor: foreground },
        cursorBlink: true,
        scrollback: 5000,
      })
      const fit = new FitAddon()
      term.loadAddon(fit)
      term.open(host)
      try {
        fit.fit()
      } catch {
        // A pane with no size yet. The observer below fits it when it gets one.
      }

      /**
       * GPU rendering, which is the other half of why this looked soft: xterm's
       * fallback is a DOM renderer, a span per glyph run laid out by the
       * browser, and it reads visibly softer than a real terminal.
       *
       * Only with a measured size, because the addon reads the render service's
       * dimensions the moment it activates and there are none before layout.
       */
      let webgl: WebglAddon | null = null
      if (host.clientWidth > 0 && host.clientHeight > 0) {
        try {
          const addon = new WebglAddon()
          // A context the browser takes back under memory pressure is not an
          // error: drop to the DOM renderer, which is the same terminal.
          addon.onContextLoss(() => {
            try {
              addon.dispose()
            } catch {
              // Already gone.
            }
          })
          term.loadAddon(addon)
          webgl = addon
        } catch {
          // No WebGL here. The DOM renderer is already what is on screen.
        }
      }

      const transport = localTerminalTransport(
        projectRef.current,
        idRef.current,
        {
          cols: term.cols,
          rows: term.rows,
        },
      )

      const offData = transport.onData((chunk) => term.write(chunk))
      const offExit = transport.onExit((code) => setExited(code))
      const input = term.onData((d) => transport.write(d))

      transport
        .open()
        .then(() => {
          if (disposed) return
          // The server's geometry is whatever the last attach asked for; ours
          // is what this pane actually is.
          transport.resize({ cols: term.cols, rows: term.rows })
          term.focus()
        })
        .catch((err: Error) => {
          if (!disposed) failedRef.current(err.message)
        })

      // The pane is resizable by definition — it is a leaf in a split — so the
      // pty's geometry follows the element rather than the window.
      const observer = new ResizeObserver(() => {
        // A pane hidden behind a maximized one (RFC 0305) measures zero. Its
        // pty keeps the size it had, so restoring redraws nothing.
        if (host.clientWidth === 0 || host.clientHeight === 0) return
        try {
          fit.fit()
        } catch {
          return
        }
        transport.resize({ cols: term.cols, rows: term.rows })
      })
      observer.observe(host)

      return () => {
        observer.disconnect()
        input.dispose()
        offData()
        offExit()
        // DETACH, not kill: this is a pane going away, and the process is not.
        transport.close()
        // The GPU addon BEFORE the terminal it renders: the other order is an
        // `_isDisposed` throw, and a throw in a cleanup is how a closing pane
        // takes the window with it.
        try {
          webgl?.dispose()
        } catch {
          // Already disposed with its context.
        }
        try {
          term.dispose()
        } catch {
          // Nothing left to tear down.
        }
      }
    }

    const raf = requestAnimationFrame(() => {
      if (disposed || !host.current) return
      teardown = build(el)
    })

    return () => {
      disposed = true
      cancelAnimationFrame(raf)
      teardown?.()
    }
  }, [])

  return (
    <div className="relative h-full min-h-0 w-full">
      {/* p-2 rather than xterm's own padding: the frame is the panel's. */}
      <div ref={host} className="h-full min-h-0 w-full p-2" />
      {exited !== null && (
        <div className="absolute inset-x-0 bottom-0 border-border border-t bg-background/90 px-3 py-1.5 text-[11px] text-muted-foreground">
          Process exited ({exited}). This shell is gone; start another from the
          back button.
        </div>
      )}
    </div>
  )
}
